import React, { useContext, useEffect, useState } from 'react'
import { useNewTranslation } from 'hooks'
import styled from 'particles/themes/styled'
import _ from 'lodash'
import Divider from 'components/Shared/Divider'
import Text from 'particles/Text'
import { theme } from 'theme'
import { BaseEditorContainer } from '../../PredefinedTaskEditor/styles'
import CustomStepEditor from './CustomStepEditor'
import { useFormikContext } from 'formik'
import { EditorValues } from '../types'
import CustomEngagementStepEditor from './CustomEngagementStepEditor'
import EmptyStepStateView from './EmptyStepView'
import { ERROR_CODES } from 'state/error'
import { ERROR_MESSAGES } from '../../errorMessages'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import Icon from 'components/Icon'
import { EDITOR_ROUTE_ENUM, ViewTypeContext } from 'components/Campaign'
import ConfirmationModal from 'components/Shared/ConfirmationModal'
import CloseButton from 'components/Shared/Button/CloseButton'
import { StepState } from 'state/tasks'
import EditCampaignRestricted from 'components/Shared/Permission/EditCampaignRestricted'
import { useSelector } from 'react-redux'
import { selectIsEngagementStepTypeHelper } from 'state/constants'

type StepsEditorProps = {
    className?: string
    campaignStatus?: CAMPAIGN_STATUS_ENUM
    onStepsChanged: VoidFunction
    showBrief?: boolean
}

const StepsEditor: React.FC<StepsEditorProps> = ({ className, campaignStatus, onStepsChanged, showBrief }) => {
    const [t] = useNewTranslation()
    const { values: formikValues, setValues, errors } = useFormikContext<EditorValues>()
    const viewType = useContext(ViewTypeContext)
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<StepState | null>(null)
    const [deleteConfirmed, setDeleteConfirmed] = useState(false)

    const handleDeleteConfirmation = () => {
        const selectedStep = showDeleteConfirmation as StepState
        setValues(currentValues => {
            return {
                ...currentValues,
                steps: currentValues.steps.filter(step => step.uuid !== selectedStep.uuid),
            }
        })
        setDeleteConfirmed(true)
    }
    useEffect(() => {
        if (deleteConfirmed) {
            onStepsChanged()
            setDeleteConfirmed(false)
        }
        setShowDeleteConfirmation(null)
    }, [deleteConfirmed])

    const handleRemoveStep = (selectedStep: StepState) => {
        setShowDeleteConfirmation(selectedStep)
    }

    const getEmptyStepView = () => {
        if (showBrief) return null
        return (
            <BaseEditorContainer>
                <EmptyStepStateView />
            </BaseEditorContainer>
        )
    }
    return (
        <div className={className}>
            {!showBrief && (
                <BaseEditorContainer>
                    <Text scale="H4" color="primary">
                        {t('campaignEditor.createTask.listOutStepsPageTitle')}
                    </Text>
                    <div style={{ height: '0.5rem' }}></div>
                    <Text color="secondary" scale="bodyS400">
                        {t('campaignEditor.createTask.fillInfoPageSubtitle')}
                    </Text>
                    <Divider maxWidth="1006px" />
                </BaseEditorContainer>
            )}

            {_.get(errors, 'platform_content_type') ===
            ERROR_MESSAGES['platform_content_type'][ERROR_CODES.INPUT_REQUIRED] ? (
                <BaseEditorContainer>
                    <Text color="error" scale="bodyS400">
                        {t('campaignEditor.createTask.fillInfoPageSubtitle')}
                    </Text>
                </BaseEditorContainer>
            ) : _.isEmpty(formikValues.steps) ? (
                getEmptyStepView()
            ) : (
                formikValues.steps.map((step, index) => (
                    <BaseEditorContainer id={step.uuid} key={step.uuid}>
                        {viewType === EDITOR_ROUTE_ENUM.EDIT && (
                            <EditCampaignRestricted campaignStatus={campaignStatus}>
                                <CloseButton
                                    size={20}
                                    color={theme.newColors.neutral400}
                                    type="button"
                                    onClick={() => handleRemoveStep(step)}
                                />
                            </EditCampaignRestricted>
                        )}
                        {isEngagementStepType(step.type) && !showBrief ? (
                            <CustomEngagementStepEditor stepIndex={index} stepUuid={step.uuid} />
                        ) : (
                            <CustomStepEditor
                                stepIndex={index}
                                campaignStatus={campaignStatus}
                                stepUuid={step.uuid}
                                showBrief={showBrief}
                            />
                        )}
                    </BaseEditorContainer>
                ))
            )}

            <ConfirmationModal
                open={Boolean(showDeleteConfirmation)}
                toggle={() => setShowDeleteConfirmation(null)}
                onConfirm={handleDeleteConfirmation}
                width="440px"
                mainIcon={<Icon.WarningCircleIcon size={'40px'} />}
                confirmText={t('button.yesDelete')}
                cancelText={t('button.noCancel')}
            >
                <Text scale="H3" color="primary" width="100%">
                    {t('modalWindow.confirmationMessage.deleteStepTitle')}
                </Text>
                <div style={{ marginBottom: theme.newSpacing.xxs }}></div>
                <Text scale="bodyM400" color="primary" width="100%">
                    {t('modalWindow.confirmationMessage.deleteStepSubtitle')}
                </Text>
            </ConfirmationModal>
        </div>
    )
}

export default styled(StepsEditor)`
    width: 100%;
    display: flex;
    flex-direction: column;
    min-height: 100%;
    height: fit-content;
    ${BaseEditorContainer} {
        background: ${theme.newColors.neutral50};
        display: flex;
        flex-direction: column;
        position: relative;
        margin-bottom: ${theme.newSpacing.l};
        height: unset;
        &:first-child {
            margin-bottom: 0;
            padding-bottom: 0;
        }
        &:last-child {
            margin-bottom: 0;
        }

        ${Divider} {
            margin-top: ${theme.newSpacing.m};
        }

        ${CloseButton} {
            position: absolute;
            top: ${theme.newSpacing.m};
            right: ${theme.newSpacing.m};
        }
    }
`
